/**
 * Google Tag Manager Helper Functions
 *
 * Provides utility functions for pushing events to GTM dataLayer.
 * Handles browser environment checks, error handling, logging, and enhanced conversions.
 *
 * Usage:
 * ```javascript
 * import { trackGTMEvent } from '@/utils/gtmHelpers';
 *
 * trackGTMEvent('signup_complete', {
 *   event_category: 'conversion',
 *   user_id: user.id,
 *   signup_method: 'email'
 * }, { email: 'user@example.com' });
 * ```
 */

import logger from './logger';
import { getStoredGclid } from './posthogAttribution';

/**
 * Prepare user data for enhanced conversions
 * Sends raw (unhashed) values — Google/GTM handles SHA-256 hashing.
 * Values are normalized (trimmed, lowercased) per Google's requirements.
 *
 * @param {Object} userData - User data object
 * @param {string} [userData.email] - User's email address
 * @param {string} [userData.phone] - User's phone number
 * @param {string} [userData.firstName] - User's first name
 * @param {string} [userData.lastName] - User's last name
 * @param {string} [userData.street] - User's street address
 * @param {string} [userData.city] - User's city
 * @param {string} [userData.region] - User's region/state (e.g., 'CA')
 * @param {string} [userData.postalCode] - User's postal code
 * @param {string} [userData.country] - User's country code (ISO 3166-1 alpha-2, e.g., 'US')
 * @returns {Object} - User data object in Google Ads format, or object with null email if no data
 */
const prepareEnhancedConversionData = (userData = {}) => {
  // If no userData provided, return structure with null email
  // Google Ads supports receiving null values for enhanced conversion data
  if (!userData || Object.keys(userData).length === 0) {
    return { email: null };
  }

  const enhancedData = {};

  // Send raw email — Google/GTM handles hashing for enhanced conversions
  const trimmedEmail = userData.email?.trim()?.toLowerCase();
  if (trimmedEmail) {
    enhancedData.email = trimmedEmail;
  } else {
    enhancedData.email = null;
  }

  // Send raw phone (cleaned to digits only)
  if (userData.phone) {
    const cleanPhone = userData.phone.replace(/\D/g, '');
    if (cleanPhone) {
      enhancedData.phone_number = cleanPhone;
    }
  }

  // Build address object if any address fields are provided
  // Google Ads expects address data in a nested 'address' object
  const hasAddressData =
    userData.firstName ||
    userData.lastName ||
    userData.street ||
    userData.city ||
    userData.region ||
    userData.postalCode ||
    userData.country;

  if (hasAddressData) {
    enhancedData.address = {};

    if (userData.firstName) {
      enhancedData.address.first_name = userData.firstName.trim().toLowerCase();
    }

    if (userData.lastName) {
      enhancedData.address.last_name = userData.lastName.trim().toLowerCase();
    }

    if (userData.street) {
      enhancedData.address.street = userData.street.trim().toLowerCase();
    }

    if (userData.city) {
      enhancedData.address.city = userData.city.trim().toLowerCase();
    }

    if (userData.region) {
      enhancedData.address.region = userData.region.trim().toLowerCase();
    }

    if (userData.postalCode) {
      enhancedData.address.postal_code = userData.postalCode.trim();
    }

    if (userData.country) {
      enhancedData.address.country = userData.country.trim().toLowerCase();
    }
  }

  return enhancedData;
};

/**
 * Safely push an event to GTM dataLayer with enhanced conversions support
 *
 * @param {string} eventName - The GTM event name (e.g., 'signup_complete', 'view_item')
 * @param {Object} properties - Event properties to include in the dataLayer push
 * @param {string} [properties.event_category] - Event category: 'conversion', 'engagement', or 'navigation'
 * @param {string} [properties.user_id] - User ID if authenticated
 * @param {*} [properties.*] - Any additional event-specific properties
 * @param {Object} [userData] - User data for enhanced conversions (email, phone, etc.)
 *
 * @example
 * trackGTMEvent('signup_complete', {
 *   event_category: 'conversion',
 *   user_id: '123',
 *   signup_method: 'email'
 * }, { email: 'user@example.com' });
 *
 * @example
 * trackGTMEvent('view_item', {
 *   event_category: 'engagement',
 *   item_id: 'model-123',
 *   item_name: 'Volkswagen Tiguan',
 *   value: 95
 * });
 */
export const trackGTMEvent = async (eventName, properties = {}, userData = null) => {
  // Check if running in browser environment
  if (typeof window === 'undefined') {
    logger.warn('[GTM] Not in browser environment, skipping event:', eventName);
    return;
  }

  // Check if dataLayer exists
  if (!window.dataLayer) {
    logger.warn('[GTM] dataLayer not initialized, skipping event:', eventName);
    return;
  }

  try {
    // Build the event object
    const eventData = {
      event: eventName,
      timestamp: Date.now(),
      ...properties,
    };

    // Add enhanced conversion data for conversion events
    // Google Ads expects the user data in 'enhanced_conversion_data' parameter
    // Always include enhanced_conversion_data for conversion events (with null values if no user data)
    // This ensures Google Ads receives consistent data structure
    if (properties.event_category === 'conversion') {
      const enhancedData = prepareEnhancedConversionData(userData);
      eventData.enhanced_conversion_data = enhancedData;

      // Retrieve stored GCLID for explicit conversion attribution
      // This ensures Google Ads can attribute the conversion even if cookies are blocked
      try {
        const gclid = await getStoredGclid();
        if (gclid) {
          eventData.gclid = gclid;
          logger.log('[GTM] GCLID added to conversion event:', gclid);
        }
      } catch (gclidError) {
        logger.warn('[GTM] Failed to retrieve GCLID:', gclidError);
      }

      if (userData?.email) {
        logger.log('[GTM] Enhanced conversion data added for:', eventName);
      } else {
        logger.log('[GTM] Conversion event with null enhanced data for:', eventName);
      }
    }

    // Push to dataLayer
    window.dataLayer.push(eventData);

    // Always log successful tracking (helps with debugging in all environments)
    logger.log('[GTM] Event tracked:', eventName, eventData);
  } catch (error) {
    logger.error('[GTM] Failed to track event:', eventName, error);
  }
};

/**
 * Check if GTM is loaded and ready
 *
 * @returns {boolean} True if GTM is loaded, false otherwise
 */
export const isGTMLoaded = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return !!(window.dataLayer && window.google_tag_manager);
};

/**
 * Get the current GTM container ID (for debugging)
 *
 * @returns {string|null} GTM container ID or null if not loaded
 */
export const getGTMContainerId = () => {
  if (typeof window === 'undefined' || !window.google_tag_manager) {
    return null;
  }

  const containerIds = Object.keys(window.google_tag_manager).filter(
    (key) => key.startsWith('GTM-')
  );

  return containerIds.length > 0 ? containerIds[0] : null;
};

/**
 * Helper function to track conversion events with enhanced conversion support
 * Automatically adds event_category: 'conversion'
 *
 * @param {string} eventName - The GTM event name
 * @param {Object} properties - Event properties
 * @param {Object} [userData] - User data for enhanced conversions (email, phone, etc.)
 */
export const trackConversion = (eventName, properties = {}, userData = null) => {
  trackGTMEvent(
    eventName,
    {
      event_category: 'conversion',
      ...properties,
    },
    userData
  );
};

/**
 * Helper function to track engagement events
 * Automatically adds event_category: 'engagement'
 *
 * @param {string} eventName - The GTM event name
 * @param {Object} properties - Event properties
 */
export const trackEngagement = (eventName, properties = {}) => {
  trackGTMEvent(eventName, {
    event_category: 'engagement',
    ...properties,
  });
};

/**
 * Track unique visit days and fire a conversion when the user visits on their 3rd unique day.
 * Uses localStorage to persist visit history across sessions.
 *
 * @param {string|null} [email] - User's email if logged in, null otherwise
 */
export const trackReturnVisitDay = (email = null) => {
  if (typeof window === 'undefined') return;

  const STORAGE_KEY = 'doocars_visit_days';
  const FIRED_KEY = 'doocars_return_visit_3rd_day_fired';

  // Already fired? Skip
  if (localStorage.getItem(FIRED_KEY)) return;

  // Get existing visit days
  let visitDays;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    visitDays = stored ? JSON.parse(stored) : [];
  } catch {
    visitDays = [];
  }

  // Add today if not already recorded
  const today = new Date().toISOString().split('T')[0];
  if (!visitDays.includes(today)) {
    visitDays.push(today);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visitDays));
  }

  // Fire conversion on 3rd unique day
  if (visitDays.length >= 3) {
    trackConversion(
      'return_visit_3rd_day',
      {
        unique_days: visitDays.length,
        first_visit: visitDays[0],
      },
      email ? { email } : null
    );
    localStorage.setItem(FIRED_KEY, 'true');
  }
};

export default {
  trackGTMEvent,
  trackConversion,
  trackEngagement,
  trackReturnVisitDay,
  isGTMLoaded,
  getGTMContainerId,
};
